export default class ContentDisposition {
    static getFilenameFromContentDispositionHeader(contentDisposition) {
        // This parser is designed to be tolerant and accepting of headers that do
        // not comply with the standard, but accepted by Firefox.

        let needsEncodingFixup = true;

        function toParamRegExp(attributePattern, flags) {
            return new RegExp(`(?:^|;)\\s*${attributePattern}\\s*=\\s*([^";\\s][^;\\s]*|"(?:[^"\\\\]|\\\\"?)+"?)`, flags);
        }

        function textdecode(encoding, value) {
            let result = value;
            if (encoding) {
                try {
                    const decoder = new TextDecoder(encoding, { fatal: true });
                    const bytes = Array.from(result as string, (c) => c.charCodeAt(0));
                    if (bytes.every((code) => code <= 0xFF)) {
                        result = decoder.decode(new Uint8Array(bytes));
                        needsEncodingFixup = false;
                    }
                } catch {
                    // TextDecoder constructor threw - unrecognized encoding.
                }
            }
            return result;
        }
        function fixupEncoding(value) {
            let result = value;
            if (needsEncodingFixup && /[\x80-\xff]/.test(value)) {
                // Maybe multi-byte UTF-8.
                result = textdecode('utf-8', value);
                if (needsEncodingFixup) {
                    // Try iso-8859-1 encoding.
                    result = textdecode('iso-8859-1', value);
                }
            }
            return result;
        }

        function rfc2616unquote(value) {
            let result = value;
            if (result.startsWith('"')) {
                const parts = result.slice(1).split('\\"');
                // Find the first unescaped " and terminate there.
                for (let i = 0; i < parts.length; ++i) {
                    const quotindex = parts[i].indexOf('"');
                    if (quotindex !== -1) {
                        parts[i] = parts[i].slice(0, quotindex);
                        parts.length = i + 1; // Truncates and stop the iteration.
                    }
                    parts[i] = parts[i].replace(/\\(.)/g, '$1');
                }
                result = parts.join('"');
            }
            return result;
        }
        function rfc5987decode(extvalue) {
            // Decodes "ext-value" from RFC 5987.
            const encodingend = extvalue.indexOf('\'');
            if (encodingend === -1) {
                // Some servers send "filename*=" without encoding'language' prefix,
                // e.g. in https://github.com/Rob--W/open-in-browser/issues/26
                // Let's accept the value like Firefox (57) (Chrome 62 rejects it).
                return extvalue;
            }
            const encoding = extvalue.slice(0, encodingend);
            const langvalue = extvalue.slice(encodingend + 1);
            // Ignore language (RFC 5987 section 3.2.1, and RFC 6266 section 4.1 ).
            const value = langvalue.replace(/^[^']*'/, '');
            return textdecode(encoding, value);
        }

        function rfc2231getparam(disposition) {
            const matches = [];
            let match;
            // Iterate over all filename*n= and filename*n*= with n being an integer
            // of at least zero. Any non-zero number must not start with '0'.
            const iter = toParamRegExp('filename\\*((?!0\\d)\\d+)(\\*?)', 'ig');
            while ((match = iter.exec(disposition)) !== null) { 
                const [, nStr, quot, part] = match; 
                const n = parseInt(nStr, 10);
                if (n in matches) {
                    // Ignore anything after the invalid second filename*0.
                    if (n === 0) break;
                    continue; 
                }
                matches[n] = [quot, part];
            }
            const parts = [];
            for (let n = 0; n < matches.length; ++n) {
                if (!(n in matches)) {
                    // Numbers must be consecutive. Truncate when there is a hole.
                    break;
                }
                const [quot, partRaw] = matches[n]; 
                let part = rfc2616unquote(partRaw);
                if (quot) {
                    part = unescape(part);
                    if (n === 0) {
                        part = rfc5987decode(part);
                    }
                }
                parts.push(part);
            }
            return parts.join('');
        }

        function rfc2047decode(value) {
            // RFC 2047-decode the result. Firefox tried to drop support for it, but
            // backed out because some servers use it - https://bugzil.la/875615
            // Firefox's condition for decoding is here: https://searchfox.org/mozilla-central/rev/4a590a5a15e35d88a3b23dd6ac3c471cf85b04a8/netwerk/mime/nsMIMEHeaderParamImpl.cpp#742-748

            // We are more strict and only recognize RFC 2047-encoding if the value
            // starts with "=?", since then it is likely that the full value is
            // RFC 2047-encoded.

            // Firefox also decodes words even where RFC 2047 section 5 states:
            // "An 'encoded-word' MUST NOT appear within a 'quoted-string'."

            
            // eslint-disable-next-line no-control-regex
            if (!value.startsWith('=?') || /[\x00-\x19\x80-\xff]/.test(value)) {
                return value;
            }
            // RFC 2047, section 2.4
            // encoded-word = "=?" charset "?" encoding "?" encoded-text "?="
            // charset = token (but let's restrict to characters that denote a
            //           possibly valid encoding).
            // encoding = q or b
            // encoded-text = any printable ASCII character other than ? or space.
            //                ... but Firefox permits ? and space.
            return value.replace(/=\?([\w-]*)\?([QqBb])\?((?:[^?]|\?(?!=))*)\?=/g,
                (_, charset, encoding, text) => {
                    let resultText = text;
                    if (encoding === 'q' || encoding === 'Q') {
                        // RFC 2047 section 4.2.
                        resultText = resultText.replace(/_/g, ' ');
                        resultText = resultText.replace(/=([0-9a-fA-F]{2})/g,
                            (__, hex) => String.fromCharCode(parseInt(hex, 16)));
                        return textdecode(charset, resultText);
                    } // else encoding is b or B - base64 (RFC 2047 section 4.1)
                    try {
                        resultText = atob(resultText);
                    } catch (e) {
                        console.warn(e);
                    }
                    return textdecode(charset, resultText);
                });
        }

        // filename*=ext-value ("ext-value" from RFC 5987, referenced by RFC 6266).
        const tmptfc6266 = toParamRegExp('filename\\*', 'i').exec(contentDisposition);
        if (tmptfc6266) {
            const tmp2 = tmptfc6266[1];
            let filename = rfc2616unquote(tmp2);
            filename = unescape(filename);
            filename = rfc5987decode(filename);
            filename = rfc2047decode(filename);
            return fixupEncoding(filename);
        }

        // Continuations (RFC 2231 section 3, referenced by RFC 5987 section 3.1).
        // filename*n*=part
        // filename*n=part
        const tmprfc2231 = rfc2231getparam(contentDisposition);
        if (tmprfc2231) {
            // RFC 2047, section
            const filename = rfc2047decode(tmprfc2231);
            return fixupEncoding(filename);
        }

        // filename=value (RFC 5987, section 4.1).
        const tmprfc5987 = toParamRegExp('filename', 'i').exec(contentDisposition);
        if (tmprfc5987) {
            const tmp2 = tmprfc5987[1];
            let filename = rfc2616unquote(tmp2);
            filename = rfc2047decode(filename);
            return fixupEncoding(filename);
        }
        return '';
    }
}
